"use client";
import { useState } from "react";
import axios from "axios";
import { LuCheck } from "react-icons/lu";
import { config } from "../../utils/config";
import { FaCircleCheck } from "react-icons/fa6";
import { Modal, Spin } from 'antd';
import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { toast } from "react-toastify";

const STATIC_IMAGES = {
    gst_image: "https://example.com/images/gst.jpg",
    pan_image: "https://example.com/images/pan.jpg",
    logo: "https://example.com/images/logo.jpg"
};

const TOAST_STYLE = {
    style: {
        background: "#fde9e9",
        borderRadius: "10px",
        padding: "14px",
        fontSize: "15px",
        border: "1px solid red"
    },
    hideProgressBar: true,
};

const isValidGST = (gst) =>
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/.test(gst);

const isValidPAN = (pan) =>
    /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);

// Looks up city/state for a 6-digit pincode via postalpincode.in
const lookupPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return null;
    try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        if (!res.ok) throw new Error(`Pincode API responded with status ${res.status}`);
        const json = await res.json();
        const postOffice = json?.[0]?.PostOffice?.[0];
        if (!postOffice) return null;
        return {
            city: postOffice.District || "",
            state: postOffice.State || "",
        };
    } catch (err) {
        console.log("Pincode lookup error:", err);
        return null;
    }
};

export default function BusinessDetailsStep({ taxType, setTaxType, data, setData, verifyGST, isPANVerified, setShowPanfield, showPanField, setIsPANVerified, verifyPanDetails, setIsGSTVerified, isGSTVerified, setPrefillBasicData, prefillBasicData }) {
    const [loading, setLoading] = useState(false);
    const { apiEndpoint } = config;

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updatedValue = value;

        if (name === "gst_number") {
            updatedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
            setIsGSTVerified(false);
        }
        if (name === "pan_number") {
            updatedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        }

        setData({
            ...data,
            [name]: updatedValue,
            ...(taxType === "gst" && { gst_image: STATIC_IMAGES.gst_image }),
            ...(taxType === "nonGst" && { pan_image: STATIC_IMAGES.pan_image }),
            logo: STATIC_IMAGES.logo
        });
    };

    // Manual pincode entry (Non-GST flow) — auto-fills city/state
    const handlePinChange = async (e) => {
        const { name, value } = e.target;
        const digitsOnly = value.replace(/\D/g, "").slice(0, 6);

        setData({
            ...data,
            [name]: digitsOnly,
        });

        if (digitsOnly.length === 6) {
            const location = await lookupPincode(digitsOnly);
            if (location) {
                setData({
                    ...data,
                    [name]: digitsOnly,
                    city: location.city,
                    state: location.state,
                });
            } else {
                setData({
                    ...data,
                    [name]: digitsOnly,
                    city: "",
                    state: "",
                });
                toast.error("Couldn't find a location for that pincode.", TOAST_STYLE);
            }
        }
    };

    const verifyGSTHandler = async () => {
        try {
            if (window.gtag) {
                window.gtag("event", "gst_verify_button_clicked", {
                    event_category: "KYC",
                    event_label: "GST Verify Button Clicked",
                });
            }
            setLoading(true);
            const res = await verifyGST(data.gst_number);
            if (res) {
                const gstData = res;
                console.log("GST RESPONSE:", gstData);

                // GST business_address format is "<STATE>, <PINCODE>" (state first, not city).
                // So derive city AND state from the pincode itself instead of splitting on comma.
                const addressParts = gstData.business_address?.split(",") || [];
                const pincode = addressParts[addressParts.length - 1]?.trim() || "";

                let city = "";
                let state = "";
                const location = await lookupPincode(pincode);
                if (location) {
                    city = location.city;
                    state = location.state;
                } else if (pincode) {
                    toast.error("GST verified, but couldn't resolve city/state from the pincode. Please fill them in manually.", TOAST_STYLE);
                }

                setData({
                    ...data,
                    gst_number: gstData.gst_number || data.gst_number,
                    company_name: gstData.business_name || "",
                    bussiness_type: gstData.bussiness_type || "",
                    pan_number: gstData.business_pan || "",
                    state,
                    city,
                    pincode,
                    gst_image: STATIC_IMAGES.gst_image,
                    logo: STATIC_IMAGES.logo,
                });
                setIsGSTVerified(true);
            } else {
                setIsGSTVerified(false);
                toast.error("GST verification failed. Please check the number.", TOAST_STYLE);
            }
        } catch (error) {
            setIsGSTVerified(false);
            console.log("GST Error:", error);
            toast.error(error?.message || "GST verification failed.", TOAST_STYLE);
        } finally {
            setLoading(false);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const verifyPanHandler = async () => {
        try {
            setIsLoading(true);
            const res = await verifyPanDetails(
                data.pan_name,
                data.pan_number
            );
            console.log("PAN RESPONSE:", res);
            setIsPANVerified(true);
            if (res?.digi_locker_url) {
                window.location.replace(res.digi_locker_url);
                setIsPANVerified(true);
            }
        } catch (err) {
            console.log("PAN Error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const verifyYourPanHandler = async () => {
        const token = sessionStorage.getItem("us_st_d");
        try {
            const response = await axios({
                url: `${apiEndpoint}/verify-pan`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`,
                },
                data: {
                    "pan_no": data?.pan_number
                }
            });

            if (response?.data?.message === "PAN verified successfully") {
                setData({
                    ...data, pan_name: `${response?.data?.data?.first_name} ${response?.data?.data?.middle_name} ${response?.data?.data?.last_name}`
                })
            } else {
                toast.error("Something went wrong..", TOAST_STYLE);
            }
        } catch (err) {
            console.log("PAN Error:", err);
            toast.error("PAN verification failed. Please try again.", TOAST_STYLE);
        }
    };

    return (
        <>
            <div>

                <h2 className="h4 fw-semibold py-1 mt-3" style={{ color: "black", fontSize: "20px" }}>
                    Business Information
                </h2>

                <div className="border shadow-sm bg-white" style={{ borderRadius: "14px" }}>

                    {/* ================= Tax Information ================= */}
                    <div className="mt-4">
                        <div className="card-body p-4 pt-0">
                            <div className="d-flex gap-3 mb-3" style={{ gap: "20px" }}>
                                <div
                                    className={`px-2 py-1 border rounded d-flex align-items-center ${taxType === "gst" ? "border-warning bg-light" : "border-secondary"}`}
                                    onClick={() => setTaxType("gst")}
                                    style={{ cursor: "pointer", width: "120px", gap: "8px" }}
                                >
                                    <input type="radio" checked={taxType === "gst"} readOnly onChange={() => {
                                        if (window.gtag) {
                                            window.gtag("event", "gst_clicked", {
                                                event_category: "Tax",
                                                event_label: "GST Selected",
                                            });
                                        }
                                    }} />
                                    <span className="ms-2">GST</span>
                                </div>

                                <div
                                    className={`px-2 py-1 border rounded d-flex align-items-center ${taxType === "nonGst" ? "border-warning bg-light" : "border-secondary"}`}
                                    onClick={() => setTaxType("nonGst")}
                                    style={{ cursor: "pointer", width: "120px", gap: "8px" }}
                                >
                                    <input type="radio" checked={taxType === "nonGst"} readOnly />
                                    <span className="ms-2">Non-GST</span>
                                </div>

                            </div>

                            {taxType === "gst" && (
                                <div className="mb-3">
                                    {isGSTVerified && data?.gst_number ? <div className="alert mb-3 alert-success mb-0 p-2" style={{ color: "green", background: "#e8f3e8" }}>
                                        <FaCircleCheck /> Your GST number has been verified successfully.
                                    </div> : null}
                                    <label className="form-label">
                                        GST Number <span className="text-dark fw-bold">*</span>
                                    </label>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            name="gst_number"
                                            value={data.gst_number || ""}
                                            onChange={handleChange}
                                            className="form-control"
                                            placeholder="Enter GST Number"
                                            style={{ background: "#F3F3F5" }}
                                            maxLength={15}
                                            readOnly={isGSTVerified && data?.gst_number ? true : false}
                                        />

                                        {isGSTVerified && data?.gst_number ? (
                                            <span className="position-absolute fw-bold text-success" style={{ right: "8px", top: "50%", transform: "translate(0px, -50%)" }}>
                                                <LuCheck />
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={verifyGSTHandler}
                                                disabled={!isValidGST(data?.gst_number) || loading}
                                                className="position-absolute text-white px-3 py-1 h-100 fw-semibold"
                                                style={{
                                                    top: "0px",
                                                    right: "0px",
                                                    background: "linear-gradient(90deg, #F75710, #FC8809)",
                                                    color: "#fff",
                                                    border: "none",
                                                    outline: "none",
                                                    borderRadius: "8px",
                                                    opacity: loading ? 0.6 : 1
                                                }}
                                            >
                                                {loading ? "Verifying..." : "Verify"}
                                            </button>
                                        )}
                                    </div>

                                    {data?.gst_number && !isValidGST(data?.gst_number) && (
                                        <small className="text-danger">Invalid GST Number</small>
                                    )}
                                </div>
                            )}

                            {taxType === "nonGst" && (
                                <>
                                    {isPANVerified && <div className="alert mb-3 alert-success mb-0 p-2" style={{ color: "green", background: "#e8f3e8" }}>
                                        <FaCircleCheck /> Your PAN details has been verified successfully.
                                    </div>}
                                    <div className="d-flex" style={{ gap: "30px" }}>

                                        <div className="mb-3 position-relative w-50">
                                            <label className="form-label">
                                                PAN Number <span className="">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                name="pan_number"
                                                value={data.pan_number || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter PAN Number"
                                                style={{ background: "#F3F3F5" }}
                                                maxLength={10}
                                            />

                                            {!isPANVerified ? <button className="btn btn-sm p-2 d-flex mt-3 justify-content-center"
                                                onClick={verifyYourPanHandler}
                                                disabled={
                                                    !isValidPAN(data.pan_number) ||
                                                    !data.pan_number ||
                                                    isLoading
                                                }
                                                style={{
                                                    width: "80px",
                                                    position: "absolute",
                                                    top: "18.2px",
                                                    right: "4px",
                                                    background: "linear-gradient(90deg, #F75710, #FC8809)",
                                                    color: "#fff",
                                                    border: "none",
                                                    outline: "none",
                                                    borderRadius: "8px",
                                                    opacity: isLoading ? 0.6 : 1,

                                                }} >
                                                {isLoading ? "Processing..." : "Verify"}
                                            </button> : ""}
                                        </div>

                                        <div className="w-50">
                                            <label className="form-label">
                                                Full Name (as per PAN)<span className="fw-bold">*</span>
                                            </label>

                                            <input
                                                type="text"
                                                name="pan_name"
                                                value={data?.pan_name || ""}
                                                placeholder="Enter Pan Name"
                                                onChange={handleChange}
                                                className="form-control"
                                                style={{ background: "#F3F3F5" }}
                                            />


                                        </div>
                                    </div>
                                    {!isPANVerified && (
                                        <button className="btn mt-3 justify-content-end d-none"
                                            onClick={verifyPanHandler}
                                            disabled={
                                                !isValidPAN(data.pan_number) ||
                                                !data.pan_name ||
                                                isLoading
                                            }
                                            style={{
                                                top: "0px",
                                                right: "0px",
                                                background: "linear-gradient(90deg, #F75710, #FC8809)",
                                                color: "#fff",
                                                border: "none",
                                                outline: "none",
                                                borderRadius: "8px",
                                                opacity: isLoading ? 0.6 : 1
                                            }} >
                                            {isLoading ? "Processing..." : "Verify PAN"}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="mx-4 border-bottom"></div>
                    </div>
                    {(
                        (taxType === "gst" && isGSTVerified && data?.gst_number) ||
                        (taxType === "nonGst")
                    ) && (
                            <>
                                <div className="p-4">
                                    <h5 className="mb-3 fw-bold" style={{ color: "#000" }}>Business Details</h5>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label>Business Type<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="bussiness_type"
                                                value={data.bussiness_type || ""}
                                                onChange={handleChange}
                                                className="form-control inputForm"
                                                placeholder="Bussiness Type"
                                                readOnly={taxType === "nonGst" ? false : true}
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label>Business Name<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="company_name"
                                                value={data.company_name || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Company Name"
                                                readOnly={taxType === "nonGst" ? false : true}
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mx-4 border-bottom"></div>

                                <div className="p-4">
                                    <h5 className="mb-3 fw-bold" style={{ color: "#000" }}>Business Address</h5>

                                    <div className="d-flex" style={{ gap: "22px" }}>
                                        <div className="w-50">
                                            <label>Street<span className="">*</span></label>
                                            <input type="text"
                                                name="street"
                                                value={data.street || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>

                                        <div className="w-50">
                                            <label>Locality<span className="">*</span></label>
                                            <input type="text"
                                                name="locality"
                                                value={data.locality || ""}
                                                onChange={handleChange}
                                                className="form-control"
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex gap-3 mt-3" style={{ gap: "22px" }}>
                                        <div className="w-50">
                                            <label>Pincode<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="pincode"
                                                value={data.pincode || ""}
                                                onChange={handlePinChange}
                                                className="form-control mb-2"
                                                placeholder="6-digit Pincode"
                                                maxLength={6}
                                                inputMode="numeric"
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                        <div className="w-50">
                                            <label>City<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={data.city || ""}
                                                className="form-control mb-2"
                                                placeholder="Auto-filled from pincode"
                                                readOnly
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                    </div>

                                    <div className="d-flex gap-3 mt-2" style={{ gap: "22px" }}>
                                        <div className="w-50">
                                            <label>State<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="state"
                                                value={data.state || ""}
                                                className="form-control mb-2"
                                                placeholder="Auto-filled from pincode"
                                                readOnly
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                        <div className="w-50">
                                            <label>Country<span className="">*</span></label>
                                            <input
                                                type="text"
                                                name="country"
                                                value={data.country || "India"}
                                                className="form-control mb-2"
                                                placeholder="Country"
                                                readOnly
                                                style={{ background: "#F3F3F5" }}
                                            />
                                        </div>
                                    </div>

                                </div>
                            </>
                        )}
                </div>
            </div>

            <Modal
                open={loading}
                footer={null}
                closable={false}
                centered
                width={450}
                style={{ borderRadius: "20px" }}

            >
                <div style={{ textAlign: "center", padding: "20px" }}>
                    <p style={{ fontWeight: "600", fontSize: "20px" }} className="m-0"> Verifying your GST Number</p>
                    <p style={{ color: "gray" }} className="mt-1">this usually takes 2-3 secs</p>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "orange" }} spin />} />
                    <p style={{ marginTop: "15px", gap: "10px", color: "gray" }} className="d-flex justify-content-center align-items-center">
                        <LockOutlined /> <span> Your details are securely processing. </span> </p>
                </div>
            </Modal>
        </>
    );
}